import React, { useState } from 'react';
import { 
  ArrowRight, ArrowLeft, Sparkles, Target, Briefcase, 
  GraduationCap, Users, Clock, Award, CheckCircle2,
  Shield, AlertTriangle, UserCheck, Ban, TrendingUp
} from 'lucide-react';
import { Button } from './ui/button';

const SUBJECT_ICONS = {
  'Anti Money Laundering': Shield,
  'Governance, Risk & Compliance': Briefcase,
  'Financial Crime Prevention': AlertTriangle,
  'Customer Due Diligence': UserCheck,
  'Managing Sanctions Risk': Ban
};

const WIZARD_STEPS = [
  {
    id: 'experience',
    question: "What's your experience level in compliance?",
    subtext: "This helps us recommend the right qualification level",
    options: [
      { 
        value: 'new', 
        label: 'New to Compliance', 
        description: 'Less than 2 years experience',
        icon: GraduationCap,
        levels: ['essential', 'certificate']
      },
      { 
        value: 'intermediate', 
        label: 'Intermediate', 
        description: '2-5 years experience',
        icon: Target,
        levels: ['certificate', 'specialist', 'advanced']
      },
      { 
        value: 'senior', 
        label: 'Senior Professional', 
        description: '5+ years experience',
        icon: Award,
        levels: ['advanced', 'diploma', 'practitioner']
      },
      { 
        value: 'executive', 
        label: 'Executive / Leadership', 
        description: 'Head of function or C-suite',
        icon: Briefcase,
        levels: ['diploma', 'practitioner', 'postgrad']
      }
    ]
  },
  {
    id: 'subject',
    question: "Which area interests you most?",
    subtext: "Select your primary focus area",
    options: [
      { 
        value: 'Anti Money Laundering', 
        label: 'Anti Money Laundering', 
        description: 'AML frameworks, risk assessment, suspicious activity',
        icon: Shield
      },
      { 
        value: 'Governance, Risk & Compliance', 
        label: 'Governance, Risk & Compliance', 
        description: 'Regulatory compliance, risk management, corporate governance',
        icon: Briefcase
      },
      { 
        value: 'Financial Crime Prevention', 
        label: 'Financial Crime Prevention', 
        description: 'Fraud prevention, bribery & corruption, investigation',
        icon: AlertTriangle
      },
      { 
        value: 'Customer Due Diligence', 
        label: 'Customer Due Diligence / KYC', 
        description: 'KYC processes, customer risk assessment',
        icon: UserCheck
      },
      { 
        value: 'Managing Sanctions Risk', 
        label: 'Managing Sanctions Risk', 
        description: 'Sanctions screening, compliance frameworks',
        icon: Ban
      }
    ]
  },
  {
    id: 'goal',
    question: "What's your primary goal?",
    subtext: "This helps us prioritize your recommendations",
    options: [
      { 
        value: 'foundation', 
        label: 'Build Foundation Knowledge', 
        description: 'Understand core concepts and frameworks',
        icon: GraduationCap,
        levelBoost: ['essential', 'certificate']
      },
      { 
        value: 'specialize', 
        label: 'Specialize in a Niche', 
        description: 'Deep expertise in a specific area',
        icon: Target,
        levelBoost: ['specialist']
      },
      { 
        value: 'advance', 
        label: 'Advance My Career', 
        description: 'Move into management or senior roles',
        icon: TrendingUp,
        levelBoost: ['advanced', 'diploma']
      },
      { 
        value: 'lead', 
        label: 'Lead a Function', 
        description: 'MLRO, Head of Compliance, or similar',
        icon: Award,
        levelBoost: ['practitioner', 'postgrad']
      }
    ]
  },
  {
    id: 'time',
    question: "How much time can you commit?",
    subtext: "All courses are flexible and self-paced",
    options: [
      { 
        value: 'short', 
        label: 'Quick Learning', 
        description: '2-4 weeks',
        icon: Clock,
        levelBoost: ['essential']
      },
      { 
        value: 'medium', 
        label: 'Part-Time Study', 
        description: '3-6 months',
        icon: Clock,
        levelBoost: ['certificate', 'specialist']
      },
      { 
        value: 'committed', 
        label: 'Committed Study', 
        description: '6-12 months',
        icon: Clock,
        levelBoost: ['advanced']
      },
      { 
        value: 'extended', 
        label: 'Extended Program', 
        description: '12-24 months',
        icon: Clock,
        levelBoost: ['diploma', 'postgrad']
      }
    ]
  },
  {
    id: 'type',
    question: "Are you enrolling as an individual or for a team?",
    subtext: "We offer in-house facilitation for corporate groups",
    options: [
      { 
        value: 'individual', 
        label: 'Individual', 
        description: 'Personal professional development',
        icon: Users
      },
      { 
        value: 'corporate', 
        label: 'Corporate / Team', 
        description: 'Training for my organization',
        icon: Briefcase,
        showInHouse: true
      }
    ]
  }
];

export default function CourseFinderWizard({ courses, onSelectCourse, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [recommendations, setRecommendations] = useState([]);

  const handleAnswer = (stepId, value, option) => {
    const newAnswers = { ...answers, [stepId]: { value, option } };
    setAnswers(newAnswers);

    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Calculate recommendations
      calculateRecommendations(newAnswers);
      setShowResults(true);
    }
  };

  const calculateRecommendations = (finalAnswers) => {
    const experienceAnswer = finalAnswers.experience;
    const subjectAnswer = finalAnswers.subject;
    const goalAnswer = finalAnswers.goal;
    const timeAnswer = finalAnswers.time;
    const typeAnswer = finalAnswers.type;

    // Get preferred levels based on experience
    const preferredLevels = experienceAnswer?.option?.levels || [];
    
    // Boost levels based on goal
    const goalBoostLevels = goalAnswer?.option?.levelBoost || [];
    
    // Boost levels based on time commitment
    const timeBoostLevels = timeAnswer?.option?.levelBoost || [];

    // Filter and score courses
    const scoredCourses = courses.map(course => {
      let score = 0;
      let matchReasons = [];

      // Subject match (highest weight)
      if (course.subject_area === subjectAnswer?.value) {
        score += 40;
        matchReasons.push('Matches your focus area');
      } else {
        // Partial match for related subjects
        score += 10;
      }

      // Level match based on experience
      if (preferredLevels.includes(course.level)) {
        score += 30;
        matchReasons.push('Right level for your experience');
      }

      // Goal alignment
      if (goalBoostLevels.includes(course.level)) {
        score += 15;
        matchReasons.push('Aligns with your career goal');
      }

      // Time commitment alignment
      if (timeBoostLevels.includes(course.level)) {
        score += 10;
        matchReasons.push('Fits your time commitment');
      }

      // In-house preference for corporate
      if (typeAnswer?.value === 'corporate' && course.pricing?.in_house_available) {
        score += 5;
        matchReasons.push('In-house facilitation available');
      }

      return {
        ...course,
        matchScore: Math.min(score, 100),
        matchReasons
      };
    });

    // Sort by score and take top results
    const sorted = scoredCourses
      .sort((a, b) => b.matchScore - a.matchScore)
      .slice(0, 6);

    setRecommendations(sorted);
  };

  const goBack = () => {
    if (showResults) {
      setShowResults(false);
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const restart = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
    setRecommendations([]);
  };

  const currentStepData = WIZARD_STEPS[currentStep];
  const progress = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  if (showResults) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Personalized Recommendations
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Your Top Course Matches</h2>
          <p className="text-slate-400">
            Based on your {answers.experience?.option?.label} experience in {answers.subject?.value}
          </p>
        </div>

        {/* Recommendations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendations.map((course, index) => {
            const SubjectIcon = SUBJECT_ICONS[course.subject_area] || Briefcase;
            return (
              <div 
                key={course.course_id}
                className={`relative p-5 rounded-xl border transition-all ${
                  index === 0 
                    ? 'bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30' 
                    : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600'
                }`}
              >
                {index === 0 && (
                  <div className="absolute -top-2.5 left-4 px-2 py-0.5 bg-amber-500 text-slate-900 text-xs font-bold rounded">
                    BEST MATCH
                  </div>
                )}

                {/* Match Score */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      index === 0 ? 'bg-amber-500/20' : 'bg-slate-700'
                    }`}>
                      <span className={`text-sm font-bold ${index === 0 ? 'text-amber-400' : 'text-slate-300'}`}>
                        {course.matchScore}%
                      </span>
                    </div>
                    <span className="text-xs text-slate-500">match</span>
                  </div>
                  {course.pricing?.in_house_available && answers.type?.value === 'corporate' && (
                    <span className="px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs">
                      In-House
                    </span>
                  )}
                </div>

                {/* Course Info */}
                <h3 className="font-semibold text-white mb-1 line-clamp-2">{course.title}</h3>
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-3">
                  <SubjectIcon className="h-3 w-3" />
                  <span>{course.level}</span>
                  <span>•</span>
                  <span>{course.duration}</span>
                </div>

                {/* Match Reasons */}
                <div className="space-y-1 mb-4">
                  {course.matchReasons.slice(0, 2).map((reason, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-400">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      {reason}
                    </div>
                  ))}
                </div>

                {/* Price */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                  <div>
                    <span className="text-lg font-bold text-white">
                      R{course.pricing?.total_zar?.toLocaleString()}
                    </span>
                    <span className="text-xs text-emerald-400 ml-2">
                      Save R{course.pricing?.savings_zar?.toLocaleString()}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => onSelectCourse(course)}
                    className={index === 0 
                      ? "bg-amber-500 hover:bg-amber-400 text-slate-900" 
                      : "bg-slate-700 hover:bg-slate-600 text-white"
                    }
                  >
                    Enquire
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-700">
          <Button
            variant="ghost"
            onClick={restart}
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Start Over
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            className="border-slate-700 text-white"
          >
            Browse All Courses
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Step {currentStep + 1} of {WIZARD_STEPS.length}</span>
          <span className="text-amber-400 font-medium">{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="text-center py-4">
        <h2 className="text-2xl font-bold text-white mb-2">{currentStepData.question}</h2>
        <p className="text-slate-400">{currentStepData.subtext}</p>
      </div>

      {/* Options */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {currentStepData.options.map((option) => {
          const Icon = option.icon;
          const isSelected = answers[currentStepData.id]?.value === option.value;
          
          return (
            <button
              key={option.value}
              onClick={() => handleAnswer(currentStepData.id, option.value, option)}
              className={`p-4 rounded-xl border text-left transition-all ${
                isSelected 
                  ? 'bg-amber-500/20 border-amber-500/50' 
                  : 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${isSelected ? 'bg-amber-500/20' : 'bg-slate-700'}`}>
                  <Icon className={`h-5 w-5 ${isSelected ? 'text-amber-400' : 'text-slate-400'}`} />
                </div>
                <div className="flex-1">
                  <h4 className={`font-medium ${isSelected ? 'text-amber-400' : 'text-white'}`}>
                    {option.label}
                  </h4>
                  <p className="text-sm text-slate-400 mt-0.5">{option.description}</p>
                </div>
                {isSelected && (
                  <CheckCircle2 className="h-5 w-5 text-amber-400 flex-shrink-0" />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-slate-700">
        <Button
          variant="ghost"
          onClick={goBack}
          disabled={currentStep === 0}
          className="text-slate-400 hover:text-white disabled:opacity-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button
          variant="outline"
          onClick={onClose}
          className="border-slate-700 text-slate-400"
        >
          Skip Wizard
        </Button>
      </div>
    </div>
  );
}
